import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { productGallery } from '../../utils/Product.js'
import { cacheDom } from '../../utils/QuerySelectors.js'
import { setZoomImageId, setZoomOpen } from '../store/productSlice'

const dom = {}
const qs = {
  swiper: 'data-gallery-swiper',
  thumbs: 'data-gallery-thumb-swiper',
}
const qsAll = {
  slideImages: 'data-slide-image',
}
const PdpGallery = () => {
  const dispatch = useDispatch()
  const { product, zoomImageId } = useSelector((state) => state.product)
  const badges = ['Collaboration', 'Coming Soon', 'pre-order']
  const imageUrl = (url, width = '1024') => {
    return url
      .replace('.jpg?', `.jpg?width=${width}&`)
      .replace('.png?', `.png?width=${width}&`)
      .replace('.webp?', `.webp?width=${width}&`)
  }
  const renderMedia = (media, high = false) => {
    if (media.media_type == 'image')
      return high ? (
        <img
          className="swiper-slide-media"
          src={imageUrl(media.preview_image.src)}
          alt={`${product.title} - ${media.id}`}
          width="1024"
          height={1024 / media.aspect_ratio}
          fetchpriority="high"
          data-slide-image={media.id}
        />
      ) : (
        <img
          className="swiper-slide-media"
          src={imageUrl(media.preview_image.src)}
          alt={`${product.title} - ${media.id}`}
          width="1024"
          height={1024 / media.aspect_ratio}
          loading="lazy"
          data-slide-image={media.id}
        />
      )
    else if (media.media_type == 'video')
      return (
        <div className="swiper-slide-video">
          <video controls>
            {media.sources.map((source, index) => (
              <source src={source.url} type={source.mime_type} key={`video-${media.id}-${source.mime_type}-${index}`} />
            ))}
            Your browser does not support the video tag.
          </video>
        </div>
      )
    else if (media.media_type == 'external_video' && media.host == 'youtube')
      return (
        <div className="swiper-slide-video">
          <iframe
            allow="accelerometer; autoplay; encrypted-media; gyroscope;"
            allowFullScreen="allowfullscreen"
            src={`https://www.youtube.com/embed/${media.external_id}?controls=1&amp;enablejsapi=1&amp;modestbranding=1&amp;origin=https%3A%2F%2Fpolinas-potent-potions.myshopify.com&amp;playsinline=1&amp;rel=0`}
            title={`Video for ${product.title}`}
          ></iframe>
        </div>
      )
    else if (media.media_type == 'external_video' && media.host == 'vimeo')
      return (
        <div className="swiper-slide-video">
          <iframe
            src={`https://player.vimeo.com/video/${media.external_id}?pip=0`}
            width="640"
            height="360"
            allow="autoplay; fullscreen;"
            allowFullScreen
            title={`Video for ${product.title}`}
          ></iframe>
        </div>
      )
  }

  const renderThumbs = (media, index) => {
    const width = product.settings.thumbnail_style == 'big' ? 80 : 40
    const mediaPosition = (index % product.media.length) + 1
    const mediaLabel = media.media_type == 'image' ? 'image' : 'video'
    return (
      <button
        type="button"
        className="appearance-none border-0 bg-transparent p-0 m-0 block w-full relative cursor-pointer"
        aria-label={`View ${mediaLabel} ${mediaPosition} of ${product.media.length}`}
      >
        <img
          className="swiper-slide-thumb"
          src={imageUrl(media.preview_image.src, width)}
          alt=""
          width={width}
          height={width / media.aspect_ratio}
          fetchpriority="high"
        />
        {media.media_type == 'image' ? null : (
          <span
            className="!text-black absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-pause'] }}
            aria-hidden="true"
          />
        )}
      </button>
    )
  }

  const minThumbSlides = product.settings.thumbnail_style == 'big' ? 12 : 16
  const shouldRepeatGallery = product.media.length > 5
  const thumbRepeatCount =
    shouldRepeatGallery && product.media.length < minThumbSlides ? Math.ceil(minThumbSlides / product.media.length) : 1
  const thumbnailMedia = Array.from({ length: thumbRepeatCount }, () => product.media).flat()

  setTimeout(() => {
    cacheDom(dom, qs, qsAll)
    const mainSwiper = productGallery(
      dom.swiper,
      dom.thumbs,
      dom.thumbs ? dom.thumbs.dataset.galleryThumbSwiper : 'small',
      zoomImageId,
    )
    const liveRegion = document.querySelector('[data-gallery-live-region]')
    if (mainSwiper && liveRegion) {
      const announceSlide = () => {
        const realIndex = typeof mainSwiper.realIndex == 'number' ? mainSwiper.realIndex : mainSwiper.activeIndex
        const media = product.media[realIndex % product.media.length]
        const mediaLabel = media && media.media_type == 'image' ? 'Image' : 'Video'
        liveRegion.textContent = `${mediaLabel} ${(realIndex % product.media.length) + 1} of ${product.media.length}`
      }
      mainSwiper.on('slideChange', announceSlide)
    }
    dom.slideImages.forEach((image) => {
      image.addEventListener('click', (e) => {
        dispatch(setZoomOpen(true))
        const imageId = parseInt(e.target.dataset.slideImage)
        if (typeof imageId != 'undefined' && imageId) dispatch(setZoomImageId(imageId))
      })
    })
  }, 300)

  return (
    <div className="relative">
      <span className="visually-hidden" aria-live="polite" data-gallery-live-region></span>
      <span
        className="w-6 h-6 block lg:hidden text-black absolute top-10 lg:top-4 right-4 z-[2] pointer-events-none"
        dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-search'] }}
        aria-hidden="true"
      />
      <div className="main-product--gallery__main" data-gallery-swiper>
        <div className="swiper-wrapper">
          {product.media.map((media, index) => (
            <div
              className="swiper-slide bg-white"
              key={`product-main-media-${media.id}`}
              data-index={index}
              data-media-id={media.id}
            >
              {renderMedia(media, index == 0)}
            </div>
          ))}
        </div>
        {product.media.length > 1 && (
          <div
            className={`swiper-pagination text-sm leading-4 lg:text-base lg:leading-6 !inline-block lg:!hidden !w-auto !left-3 !bottom-3 ${product.settings.thumbnail_style == 'small' ? 'lg:!left-6 lg:!bottom-6{% else %} lg:!left-[4.5rem] lg:!bottom-[3.875rem]' : ''}`}
          ></div>
        )}
        {product.media.length > 1 && product.settings.thumbnail_style == 'big' && (
          <button
            className="swiper-button-prev focus:outline !left-4 lg:!left-6 !bottom-1/2 lg:!bottom-[3.8rem] after:!hidden !top-[initial] !h-auto !flex !text-black"
            dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-arrow'] }}
          />
        )}
        {product.media.length > 1 && product.settings.thumbnail_style == 'big' && (
          <button
            className="swiper-button-next focus:outline !right-4 lg:!right-6 !bottom-1/2 lg:!bottom-[3.8rem] after:!hidden !top-[initial] !h-auto !flex !text-black"
            dangerouslySetInnerHTML={{ __html: window.globalIcons['icon-arrow-right'] }}
          />
        )}
      </div>
      {product.media.length > 1 && (
        <div
          className={`main-product--gallery__thumb main-product--gallery__thumb--${product.settings.thumbnail_style}`}
          data-gallery-thumb-swiper={product.settings.thumbnail_style}
        >
          <div className="swiper-wrapper">
            {thumbnailMedia.map((media, index) => (
              <div
                className="swiper-slide"
                data-gallery-index={index % product.media.length}
                data-swiper-slide-index={index % product.media.length}
                key={`product-thumb-${media.id}-${index}`}
              >
                {renderThumbs(media, index)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="main-product--badges">
        {product.tags.includes('Final Sale') && <div className="product-badge">Final Sale</div>}
        {product.comparePrice > product.price && !product.tags.includes('Final Sale') && (
          <div className="product-badge">Sale</div>
        )}
        {badges.map((badge, index) =>
          product.tags.includes(badge) ? (
            <div className="product-badge" key={`badge-${product.id}-${index}`}>
              {badge}
            </div>
          ) : null,
        )}
      </div>
    </div>
  )
}

export default PdpGallery
